// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Button,
  Icon,
  Message,
  Dropdown,
  DropdownProps,
} from 'semantic-ui-react';
import { supportStaffApi } from '../api/supportStaffApi';
import { advisorApi, Advisor } from '../api/advisorApi';
import type { Brokerage } from '../api/brokerageApi';
import type { SupportStaffInvite } from '../types/supportStaff';

interface InviteSupportStaffModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  brokerages: Brokerage[];
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  brokerageId: string;
  initialAdvisorIds: string[];
  notes: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  brokerageId?: string;
}

function InviteSupportStaffModal({
  open,
  onClose,
  onSuccess,
  brokerages,
}: Readonly<InviteSupportStaffModalProps>) {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    brokerageId: '',
    initialAdvisorIds: [],
    notes: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [advisors, setAdvisors] = useState<Advisor[]>([]);
  const [loadingAdvisors, setLoadingAdvisors] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        brokerageId: '',
        initialAdvisorIds: [],
        notes: '',
      });
      setErrors({});
      setSubmitError(null);
      setAdvisors([]);
    }
  }, [open]);

  // Load advisors when brokerage is selected
  useEffect(() => {
    if (formData.brokerageId) {
      loadAdvisorsForBrokerage(formData.brokerageId);
    } else {
      setAdvisors([]);
      setFormData((prev) => ({ ...prev, initialAdvisorIds: [] }));
    }
  }, [formData.brokerageId]);

  const loadAdvisorsForBrokerage = async (brokerageId: string) => {
    setLoadingAdvisors(true);
    try {
      const response = await advisorApi.getByBrokerage(brokerageId, 0, 100);
      // Filter to only show active advisors with ADMIN or USER role (not SUPPORT)
      setAdvisors(
        response.content.filter(
          (a) => a.status === 'active' && (a.role === 'ADMIN' || a.role === 'USER')
        )
      );
    } catch (err) {
      console.error('Failed to load advisors:', err);
      setAdvisors([]);
    } finally {
      setLoadingAdvisors(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.length > 100) {
      newErrors.firstName = 'First name must be 100 characters or less';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.length > 100) {
      newErrors.lastName = 'Last name must be 100 characters or less';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.brokerageId) {
      newErrors.brokerageId = 'Brokerage is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const invite: SupportStaffInvite = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        brokerageId: formData.brokerageId,
        phoneNumber: formData.phoneNumber.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        initialAdvisorIds:
          formData.initialAdvisorIds.length > 0 ? formData.initialAdvisorIds : undefined,
      };

      await supportStaffApi.invite(invite);
      onSuccess();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to invite support staff');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleBrokerageChange = (_: React.SyntheticEvent, data: DropdownProps) => {
    handleInputChange('brokerageId', data.value as string);
  };

  const handleAdvisorsChange = (_: React.SyntheticEvent, data: DropdownProps) => {
    handleInputChange('initialAdvisorIds', data.value as string[]);
  };

  const brokerageOptions = brokerages.map((b) => ({
    key: b.id,
    value: b.id,
    text: b.name,
  }));

  const advisorOptions = advisors.map((a) => ({
    key: a.id,
    value: a.id,
    text: `${a.firstName} ${a.lastName}`,
    description: a.email,
  }));

  const getAdvisorPlaceholder = () => {
    if (!formData.brokerageId) return 'Select a brokerage first';
    if (loadingAdvisors) return 'Loading advisors...';
    return 'Select advisors to assign...';
  };

  return (
    <Modal open={open} onClose={onClose} size="small" closeIcon>
      <Modal.Header>
        <Icon name="user plus" /> Invite New Support Staff
      </Modal.Header>
      <Modal.Content>
        {submitError && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{submitError}</p>
          </Message>
        )}

        <Form>
          <Form.Group widths="equal">
            <Form.Input
              label="First Name"
              placeholder="Enter first name"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              error={errors.firstName ? { content: errors.firstName } : undefined}
              required
            />
            <Form.Input
              label="Last Name"
              placeholder="Enter last name"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              error={errors.lastName ? { content: errors.lastName } : undefined}
              required
            />
          </Form.Group>

          <Form.Input
            label="Email Address"
            placeholder="Enter email address"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={errors.email ? { content: errors.email } : undefined}
            required
          />

          <Form.Input
            label="Phone Number"
            placeholder="Enter phone number (optional)"
            value={formData.phoneNumber}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
          />

          <Form.Field required error={!!errors.brokerageId}>
            <label htmlFor="invite-brokerage">Brokerage</label>
            <Dropdown
              id="invite-brokerage"
              placeholder="Select brokerage..."
              fluid
              selection
              options={brokerageOptions}
              value={formData.brokerageId}
              onChange={handleBrokerageChange}
            />
            {errors.brokerageId && (
              <span style={{ color: '#9f3a38', fontSize: '0.85em' }}>
                {errors.brokerageId}
              </span>
            )}
          </Form.Field>

          <Form.Field>
            <label htmlFor="invite-initial-advisors">Initial Advisor Assignments (Optional)</label>
            <Dropdown
              id="invite-initial-advisors"
              placeholder={getAdvisorPlaceholder()}
              fluid
              multiple
              selection
              options={advisorOptions}
              value={formData.initialAdvisorIds}
              onChange={handleAdvisorsChange}
              disabled={!formData.brokerageId || loadingAdvisors}
              loading={loadingAdvisors}
            />
            {formData.initialAdvisorIds.length > 0 && (
              <small style={{ color: '#666' }}>
                Selected: {formData.initialAdvisorIds.length} advisor
                {formData.initialAdvisorIds.length === 1 ? '' : 's'}
              </small>
            )}
          </Form.Field>

          <Form.TextArea
            label="Notes (Internal)"
            placeholder="Add internal notes about this support staff member..."
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            rows={3}
          />

          <Message info size="small">
            <Message.Content>
              <Icon name="info circle" />
              An invitation email will be sent to the support staff. They will set their
              password upon accepting the invitation.
            </Message.Content>
          </Message>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button primary onClick={handleSubmit} loading={submitting}>
          <Icon name="send" /> Send Invitation
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default InviteSupportStaffModal;
